/**
 * Config-driven header script embedding (RPP-259).
 *
 * Admins paste vendor snippets (Salesforce Embedded Messaging, consent managers,
 * Piwik/Matomo, …) into AppConfig `PPHeaderEmbedScripts`. The portal injects them
 * into document.head at runtime so upgrades never require re-patching static HTML.
 *
 * Script tags cannot be inserted via innerHTML — browsers strip/ignore execution —
 * so we parse tags and create real HTMLScriptElement nodes.
 */

export const EMBED_SCRIPT_ATTR = 'data-pp-embed-script';

/** Salesforce Messaging for In-App and Web bootstrap surface (partial). */
export interface EmbeddedMessagingBootstrap {
  utilAPI?: {
    launchChat?: () => Promise<void> | void;
    removeAllComponents?: () => Promise<void> | void;
  };
  userVerificationAPI?: {
    clearSession?: () => Promise<void> | void;
  };
  prechatAPI?: {
    setVisiblePrechatFields?: (fields: Record<string, { value: string }>) => void;
    setHiddenPrechatFields?: (fields: Record<string, string>) => void;
  };
  settings?: Record<string, unknown>;
  init?: (...args: unknown[]) => void;
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: EmbeddedMessagingBootstrap;
  }
}

export interface EmbedScriptDescriptor {
  src?: string;
  type?: string;
  async?: boolean;
  defer?: boolean;
  onload?: string;
  text?: string;
}

/**
 * Replace Gen2-style `@wSiteGroup` (and optional `{SiteGroup}`) with the active
 * site-group code before injection.
 */
export function resolveEmbedScriptTokens(html: string, siteGroupCode: string): string {
  const code = siteGroupCode.trim();
  if (!code) return html;
  return html
    .replace(/@wSiteGroup\b/gi, code)
    .replace(/\{SiteGroup\}/gi, code);
}

/** Decode encoded script wrappers, preserving JavaScript once real tags exist. */
export function decodeEmbedHtml(html: string): string {
  let current = html;
  for (let i = 0; i < 2; i++) {
    if (new DOMParser().parseFromString(current, 'text/html').querySelector('script')) break;
    if (!/^\s*&(?:amp;)*lt;\s*script\b/i.test(current)) break;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = current;
    if (textarea.value === current) break;
    current = textarea.value;
  }
  return current;
}

function mapScriptNode(node: HTMLScriptElement): EmbedScriptDescriptor {
  const src = node.getAttribute('src') ?? undefined;
  const type = node.getAttribute('type') ?? undefined;
  const onload = node.getAttribute('onload') ?? undefined;
  const text = src ? undefined : (node.textContent ?? '');
  return {
    src: src || undefined,
    type: type || undefined,
    async: node.hasAttribute('async'),
    defer: node.hasAttribute('defer'),
    onload: onload || undefined,
    text,
  };
}

export function parseEmbedScripts(html: string): EmbedScriptDescriptor[] {
  const trimmed = decodeEmbedHtml(html.trim());
  if (!trimmed) return [];

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  const nodes = Array.from(doc.querySelectorAll('script'));
  if (nodes.length > 0) return nodes.map(mapScriptNode);

  if (/^https?:\/\/\S+\.js(\?\S*)?$/i.test(trimmed)) {
    return [{ src: trimmed, async: false }];
  }

  // Raw Salesforce JS pasted without <script> wrappers.
  if (/initEmbeddedMessaging|embeddedservice_bootstrap/i.test(trimmed)) {
    return [{ text: trimmed, type: 'text/javascript' }];
  }

  return [];
}

let generation = 0;
let injectionController: AbortController | undefined;
let cleanup: Promise<void> = Promise.resolve();
let pendingCleanups = 0;
let lastEmbedAttempt: LastEmbedAttempt | null = null;

export interface LastEmbedAttempt {
  htmlLength: number;
  parsedCount: number;
  injectedCount: number;
  skipReason?: string;
  preview: string;
}

export function getLastEmbedAttempt(): LastEmbedAttempt | null {
  return lastEmbedAttempt;
}

export function recordEmbedSkip(reason: string, html: string): void {
  const trimmed = html.trim();
  lastEmbedAttempt = {
    htmlLength: trimmed.length,
    parsedCount: parseEmbedScripts(trimmed).length,
    injectedCount: 0,
    skipReason: reason,
    preview: trimmed.slice(0, 80),
  };
}

const pendingLoads = new Set<Promise<void>>();
let pendingChatLaunch: {
  generation: number;
  promise: Promise<boolean>;
  cancel: () => void;
} | undefined;

/**
 * Remove previously injected embed scripts and inject the parsed descriptors.
 * Waits for previous loads and vendor teardown before injecting. Callers should
 * only call when the payload or selected site group changes.
 */
export async function injectEmbedScripts(html: string, siteGroupCode: string, root: ParentNode = document.head): Promise<number> {
  const pendingCleanup = clearEmbedScripts(root);
  const currentGeneration = generation;
  await pendingCleanup;
  if (currentGeneration !== generation) return 0;
  const controller = new AbortController();
  injectionController = controller;
  const { signal } = controller;
  const resolved = resolveEmbedScriptTokens(html, siteGroupCode);
  const descriptors = parseEmbedScripts(resolved);
  lastEmbedAttempt = {
    htmlLength: resolved.trim().length,
    parsedCount: descriptors.length,
    injectedCount: 0,
    preview: resolved.trim().slice(0, 80),
  };
  if (resolved.trim() && descriptors.length === 0) {
    console.warn('[LiveChat] PPHeaderEmbedScripts is not injectable HTML <script> tags', lastEmbedAttempt);
  }
  let count = 0;
  for (const desc of descriptors) {
    if (signal.aborted) break;
    const el = document.createElement('script');
    el.setAttribute(EMBED_SCRIPT_ATTR, '1');
    if (desc.type) el.type = desc.type;
    el.async = desc.async ?? false;
    if (desc.defer) el.defer = true;
    // Dynamic inline scripts do not wait for earlier external scripts. Await
    // non-async loads explicitly before appending their dependent snippets.
    const waitsForLoad = !desc.async && (!!desc.src || desc.type === 'module');
    let complete: ((loaded: boolean) => void) | undefined;
    let fail: ((error: Error) => void) | undefined;
    const loaded = waitsForLoad ? new Promise<boolean>((resolve, reject) => {
      complete = resolve;
      fail = reject;
    }) : undefined;
    let finishLoad: (() => void) | undefined;
    if (desc.src || desc.type === 'module') {
      const pending = new Promise<void>((resolve) => { finishLoad = resolve; });
      pendingLoads.add(pending);
      void pending.then(() => pendingLoads.delete(pending));
    }
    const onAbort = () => {
      complete?.(false);
    };
    signal.addEventListener('abort', onAbort, { once: true });
    el.onload = () => {
      finishLoad?.();
      signal.removeEventListener('abort', onAbort);
      if (signal.aborted) return;
      try {
        if (desc.onload) {
          // eslint-disable-next-line no-new-func -- admin-authored portal config
          new Function(desc.onload)();
        }
        complete?.(true);
      } catch (err) {
        console.error('PPHeaderEmbedScripts onload failed:', err);
        fail?.(err instanceof Error ? err : new Error(String(err)));
      }
    };
    el.onerror = () => {
      finishLoad?.();
      signal.removeEventListener('abort', onAbort);
      if (signal.aborted) return;
      const error = new Error('PPHeaderEmbedScripts script failed to load');
      if (fail) fail(error);
      else console.error(error);
    };
    if (desc.src) {
      el.src = desc.src;
    } else if (desc.text) {
      el.text = desc.text;
    }
    root.appendChild(el);
    count++;
    if (loaded && !await loaded) break;
  }
  if (lastEmbedAttempt) lastEmbedAttempt = { ...lastEmbedAttempt, injectedCount: count };
  return count;
}

export function clearEmbedScripts(root: ParentNode = document.head): Promise<void> {
  pendingChatLaunch?.cancel();
  generation++;
  injectionController?.abort();
  injectionController = undefined;
  root.querySelectorAll(`script[${EMBED_SCRIPT_ATTR}]`).forEach((el) => el.remove());
  // Removing a script tag cannot undo a request already started by the browser.
  // Drain those loads before teardown so an old bootstrap cannot overwrite the
  // next facility's global after its widget has started.
  const loads = [...pendingLoads];
  pendingCleanups++;
  cleanup = cleanup.catch(() => {}).then(async () => {
    let bootstrap: EmbeddedMessagingBootstrap | undefined;
    try {
      await Promise.all(loads);
      bootstrap = window.embeddedservice_bootstrap;
      try {
        await bootstrap?.userVerificationAPI?.clearSession?.();
      } finally {
        await bootstrap?.utilAPI?.removeAllComponents?.();
      }
    } finally {
      if (window.embeddedservice_bootstrap === bootstrap) {
        delete window.embeddedservice_bootstrap;
      }
      pendingCleanups--;
    }
  });
  return cleanup;
}

function getBootstrap(): EmbeddedMessagingBootstrap | undefined {
  const bootstrap = typeof window !== 'undefined' ? window.embeddedservice_bootstrap : undefined;
  return pendingCleanups > 0 ? undefined : bootstrap;
}

function hasLaunchApi(): boolean {
  return typeof getBootstrap()?.utilAPI?.launchChat === 'function';
}

function hasEmbedScripts(): boolean {
  return typeof document !== 'undefined'
    && document.querySelector(`script[${EMBED_SCRIPT_ATTR}]`) !== null;
}

export interface LiveChatDiagnostics {
  embedScriptCount: number;
  hasBootstrap: boolean;
  hasLaunchChat: boolean;
  cleanupInProgress: boolean;
  lastEmbedAttempt: LastEmbedAttempt | null;
}

export function getLiveChatDiagnostics(): LiveChatDiagnostics {
  const bootstrap = typeof window !== 'undefined' ? window.embeddedservice_bootstrap : undefined;
  return {
    embedScriptCount: typeof document === 'undefined'
      ? 0
      : document.querySelectorAll(`script[${EMBED_SCRIPT_ATTR}]`).length,
    hasBootstrap: bootstrap !== undefined,
    hasLaunchChat: typeof bootstrap?.utilAPI?.launchChat === 'function',
    cleanupInProgress: pendingCleanups > 0,
    lastEmbedAttempt,
  };
}

/** Default wait for Salesforce to become ready. Keep short so FAB clicks fail fast. */
export const LIVE_CHAT_READY_TIMEOUT_MS = 1_500;
/** Allow dependency loading after Salesforce accepts the launch request. */
export const LIVE_CHAT_LAUNCH_TIMEOUT_MS = 15_000;

/**
 * Launch Salesforce Embedded Messaging chat.
 *
 * Concurrent clicks share one attempt. Success requires vendor completion or
 * the conversation-opened event. If the widget is not on the page, fail
 * immediately instead of waiting out the timeout.
 */
export function launchLiveChat(
  readyTimeoutMs = LIVE_CHAT_READY_TIMEOUT_MS,
  launchTimeoutMs = LIVE_CHAT_LAUNCH_TIMEOUT_MS,
): Promise<boolean> {
  const currentGeneration = generation;
  if (pendingChatLaunch?.generation === currentGeneration) return pendingChatLaunch.promise;
  if (!hasLaunchApi() && !hasEmbedScripts()) {
    return Promise.resolve(false);
  }
  let cancel = () => {};
  const promise = new Promise<boolean>((resolve) => {
    let settled = false;
    let launching = false;
    let timer: number;
    const isCurrent = () => currentGeneration === generation && pendingCleanups === 0;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('onEmbeddedMessagingReady', invokeLaunch);
      window.removeEventListener('onEmbeddedMessagingButtonCreated', invokeLaunch);
      window.removeEventListener('onEmbeddedMessagingConversationOpened', onOpened);
      window.clearTimeout(timer);
      if (pendingChatLaunch?.generation === currentGeneration) pendingChatLaunch = undefined;
      resolve(ok);
    };
    cancel = () => finish(false);
    const invokeLaunch = () => {
      if (settled || launching) return;
      if (!isCurrent()) {
        finish(false);
        return;
      }
      const api = getBootstrap()?.utilAPI;
      if (typeof api?.launchChat !== 'function') return;
      launching = true;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => finish(false), launchTimeoutMs);
      // A Promise boundary catches synchronous vendor exceptions as well.
      void Promise.resolve().then(() => {
        if (settled || !isCurrent()) return false;
        return Promise.resolve(api.launchChat()).then(() => true);
      }).then((ok) => finish(ok && isCurrent()), (err: unknown) => {
        console.error('Embedded Messaging launchChat failed:', err);
        finish(false);
      });
    };
    const onOpened = () => {
      if (launching) finish(isCurrent());
    };
    window.addEventListener('onEmbeddedMessagingButtonCreated', invokeLaunch);
    window.addEventListener('onEmbeddedMessagingReady', invokeLaunch);
    window.addEventListener('onEmbeddedMessagingConversationOpened', onOpened);
    timer = window.setTimeout(() => finish(false), readyTimeoutMs);
    // Start after pendingChatLaunch has been registered, including for vendors
    // that dispatch their conversation-opened event synchronously.
    void Promise.resolve().then(invokeLaunch);
  });
  pendingChatLaunch = { generation: currentGeneration, promise, cancel };
  return promise;
}
